"use strict";

/**
 * WEB-4489: shared helpers for developer email notification schedules
 * (`developer_email_notifications` rows, WEB-4488 on the server).
 *
 * Used by DMS → Settings → Email Notifications (developer_settings.js), by the
 * "Commission claimable email" section on DMS → Project → Agencies (property/agency.js) and,
 * WEB-4823, by the "Sale Applications Email" section on DMS → Project → Law Firms
 * (property/lawfirms/sale_applications_email.js). Each pair of screens edits the same rows, so the
 * frequency / day / recipient rules live here once.
 *
 * @remarks The codes mirror the server constants FREQUENCY_*, EMAIL_NOTIFICATION_AGENCY_COMMISSION_CLAIMABLE
 *          and EMAIL_NOTIFICATION_LAWFIRM_SALE_APPLICATIONS (ShowsuiteServer app/Common/constants.php).
 *          Fortnightly is accepted by the server for the agency commission rule (report code 15) and the
 *          law firm sale applications rule (report code 16) only.
 */
class SSEmailNotificationScheduleService {

    constructor() {
        const self = this;

        self.FREQUENCY_DAILY = 1;
        self.FREQUENCY_WEEKLY = 2;
        self.FREQUENCY_MONTHLY = 3;
        self.FREQUENCY_FORTNIGHTLY = 4;

        self.REPORT_CODE_AGENCY_COMMISSION_CLAIMABLE = 15;
        // WEB-4823: a law firm's sale applications (report R111), scoped to one law firm
        self.REPORT_CODE_LAWFIRM_SALE_APPLICATIONS = 16;

        /** Server limit on recipients per rule (EmailNotificationSettingService::MAX_RECIPIENTS). */
        self.MAX_RECIPIENTS = 50;

        /** `schedule_day` for "last day of the month" on a monthly rule. */
        self.MONTH_DAY_LAST = 0;

        /** ISO weekdays, as stored in `schedule_day` for weekly rules. */
        self.weekdayOptions = [
            {id: 1, value: 'Monday'},
            {id: 2, value: 'Tuesday'},
            {id: 3, value: 'Wednesday'},
            {id: 4, value: 'Thursday'},
            {id: 5, value: 'Friday'},
            {id: 6, value: 'Saturday'},
            {id: 7, value: 'Sunday'},
        ];

        /** Days of the month for monthly rules: 1–31, then 0 = last day of the month. */
        self.monthDayOptions = [];
        for (let day = 1; day <= 31; day++) {
            self.monthDayOptions.push({id: day, value: self.ordinal(day)});
        }
        self.monthDayOptions.push({id: self.MONTH_DAY_LAST, value: 'Last day of month'});
    }

    /**
     * Frequency options for a rule's report type.
     *
     * @param {boolean} includeFortnightly True for report codes 15 and (WEB-4823) 16.
     * @returns {Array<{id: number, value: string}>}
     */
    getFrequencyOptions(includeFortnightly) {
        const self = this;

        let options = [
            {id: self.FREQUENCY_DAILY, value: 'Daily'},
            {id: self.FREQUENCY_WEEKLY, value: 'Weekly'},
        ];
        if (includeFortnightly) {
            options.push({id: self.FREQUENCY_FORTNIGHTLY, value: 'Fortnightly'});
        }
        options.push({id: self.FREQUENCY_MONTHLY, value: 'Monthly'});

        return options;
    }

    /**
     * @param {number} day 1–31
     * @returns {string} e.g. "1st", "22nd"
     */
    ordinal(day) {
        let tens = day % 100;
        if (tens >= 11 && tens <= 13) {
            return day + 'th';
        }

        return day + ({1: 'st', 2: 'nd', 3: 'rd'}[day % 10] || 'th');
    }

    /**
     * Number-or-null, for a value read from the API or a select model.
     *
     * @param {*} value
     * @returns {number|null}
     */
    toNumberOrNull(value) {
        if (value === undefined || value === null || value === '') {
            return null;
        }
        let number = Number(value);

        return isNaN(number) ? null : number;
    }

    /**
     * One-line, human-readable schedule of a rule, for the list screens.
     *
     * @param {Object} rule A developer_email_notifications row (frequency, schedule_day, schedule_start_date).
     * @returns {string} e.g. "Weekly on Tuesday", "Monthly on the last day", "Fortnightly from 05/10/2026", or "--".
     *
     * @remarks A NULL schedule_day keeps the server's original behaviour (weekly = Monday,
     *          monthly = the 1st), so it is described that way rather than as "not set".
     */
    describeSchedule(rule) {
        const self = this;

        if (!rule) {
            return '--';
        }

        let frequency = self.toNumberOrNull(rule.frequency);
        let day = self.toNumberOrNull(rule.schedule_day);

        if (frequency === self.FREQUENCY_DAILY) {
            return 'Daily';
        }
        if (frequency === self.FREQUENCY_WEEKLY) {
            let weekday = self.weekdayOptions.find(x => x.id === (day === null ? 1 : day));
            return 'Weekly on ' + (weekday ? weekday.value : 'Monday');
        }
        if (frequency === self.FREQUENCY_MONTHLY) {
            if (day === self.MONTH_DAY_LAST) {
                return 'Monthly on the last day';
            }
            return 'Monthly on the ' + self.ordinal(day === null ? 1 : day);
        }
        if (frequency === self.FREQUENCY_FORTNIGHTLY) {
            let start = self.formatDisplayDate(rule.schedule_start_date);
            return 'Fortnightly' + (start ? ' from ' + start : '');
        }

        return '--';
    }

    /**
     * @param {string|null} ymd "Y-m-d" (or a longer ISO string) from the API.
     * @returns {string} "DD/MM/YYYY", or '' when empty / unparseable.
     */
    formatDisplayDate(ymd) {
        let date = this.toDate(ymd);
        if (!date) {
            return '';
        }

        let pad = n => (n < 10 ? '0' : '') + n;

        return pad(date.getDate()) + '/' + pad(date.getMonth() + 1) + '/' + date.getFullYear();
    }

    /**
     * @param {string|null} value A timestamp from the API, e.g. `last_sent_at`
     *        ("2026-10-01T00:05:12.000000Z", or "2026-10-01 08:05:12" from older serialisers).
     * @returns {string} Local "DD/MM/YYYY HH:mm", the raw value when unparseable, or '' when empty.
     *
     * @remarks An ISO value with a zone is converted to the viewer's time; a plain
     *          "Y-m-d H:i:s" is shown as is (it carries no zone to convert from).
     */
    formatDisplayDateTime(value) {
        if (!value) {
            return '';
        }
        let text = String(value);
        let pad = n => (n < 10 ? '0' : '') + n;

        let plain = text.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/);
        if (plain) {
            return plain[3] + '/' + plain[2] + '/' + plain[1] + ' ' + plain[4] + ':' + plain[5];
        }

        let date = new Date(text);
        if (isNaN(date.getTime())) {
            return text;
        }

        return pad(date.getDate()) + '/' + pad(date.getMonth() + 1) + '/' + date.getFullYear()
            + ' ' + pad(date.getHours()) + ':' + pad(date.getMinutes());
    }

    /**
     * Parse an API "Y-m-d" into a local Date for an `<input type="date">` model.
     *
     * @param {string|null} ymd
     * @returns {Date|null}
     *
     * @remarks Built from the parts, not `new Date('2026-10-05')`, which is read as UTC midnight
     *          and shows the previous day in timezones west of UTC.
     */
    toDate(ymd) {
        if (!ymd || typeof ymd !== 'string') {
            return null;
        }
        let match = ymd.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (!match) {
            return null;
        }

        return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    }

    /**
     * @param {Date|null|undefined} date An `<input type="date">` model (undefined while the typed value is invalid).
     * @returns {string|null} "Y-m-d"
     */
    toYmd(date) {
        // toString tag rather than instanceof, so a Date from another frame / realm is accepted
        if (Object.prototype.toString.call(date) !== '[object Date]' || isNaN(date.getTime())) {
            return null;
        }
        let pad = n => (n < 10 ? '0' : '') + n;

        return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate());
    }

    /**
     * @param {string|null} emails Comma-separated recipients.
     * @returns {string[]} Trimmed, non-empty, de-duplicated addresses (same normalisation as the server).
     */
    splitEmails(emails) {
        if (!emails) {
            return [];
        }
        let seen = {};

        return String(emails).split(',')
            .map(email => email.trim())
            .filter(email => {
                if (!email || seen[email]) {
                    return false;
                }
                seen[email] = true;
                return true;
            });
    }

    /**
     * Client-side check of a recipient list, matching the server's rule for report codes 15 and 16.
     *
     * @param {string|null} emails Comma-separated recipients.
     * @returns {string|null} An error message, or null when valid.
     */
    validateEmails(emails) {
        const self = this;

        let list = self.splitEmails(emails);
        if (!list.length) {
            return 'Please enter at least one email address.';
        }
        if (list.length > self.MAX_RECIPIENTS) {
            return 'No more than ' + self.MAX_RECIPIENTS + ' email addresses are allowed.';
        }
        let invalid = list.filter(email => !/^[^\s@,]+@[^\s@,]+\.[^\s@,]+$/.test(email));
        if (invalid.length) {
            return 'Invalid email address: ' + invalid.join(', ');
        }

        return null;
    }

    /**
     * Client-side check of the schedule fields.
     *
     * @param {Object} params {frequency, schedule_day}
     * @param {Date|null|undefined} startDate The fortnightly start date model.
     * @param {boolean} allowFortnightly True for report codes 15 and 16.
     * @returns {Object} field => message; empty when valid.
     */
    validateSchedule(params, startDate, allowFortnightly) {
        const self = this;

        let errors = {};
        let frequency = self.toNumberOrNull(params.frequency);

        if (frequency === null) {
            errors.frequency = 'Please select notification frequency';
        } else if (frequency === self.FREQUENCY_FORTNIGHTLY && !allowFortnightly) {
            errors.frequency = 'Fortnightly is only available for the agency commission and law firm sale applications notifications.';
        } else if (frequency === self.FREQUENCY_FORTNIGHTLY && !self.toYmd(startDate)) {
            errors.schedule_start_date = 'Please select a start date for a fortnightly notification.';
        }

        return errors;
    }

    /**
     * The schedule columns to send, cleared where the frequency does not use them.
     *
     * @param {Object} params {frequency, schedule_day}
     * @param {Date|null|undefined} startDate
     * @returns {{frequency: (number|null), schedule_day: (number|null), schedule_start_date: (string|null)}}
     *
     * @remarks Fortnightly sends no schedule_day: the server derives it from the start date.
     */
    buildSchedule(params, startDate) {
        const self = this;

        let frequency = self.toNumberOrNull(params.frequency);
        let day = self.toNumberOrNull(params.schedule_day);

        return {
            frequency: frequency,
            schedule_day: (frequency === self.FREQUENCY_WEEKLY || frequency === self.FREQUENCY_MONTHLY) ? day : null,
            schedule_start_date: frequency === self.FREQUENCY_FORTNIGHTLY ? self.toYmd(startDate) : null,
        };
    }

    /**
     * Per-field messages from a 412001 response, every message of a field joined.
     *
     * @param {Error} response The SSHttpService error object (field messages are in response.data).
     * @returns {Object} field => message
     *
     * @remarks SSHttpService.parseErrors() keeps only the first message per field, which hides all
     *          but one bad address in a recipient list.
     */
    parseFieldErrors(response) {
        let errors = {};
        let data = response && response.data;
        if (!data || typeof data !== 'object') {
            return errors;
        }

        Object.keys(data).forEach(function (field) {
            let messages = data[field];
            if (Array.isArray(messages)) {
                errors[field] = messages.join(' ');
            } else if (typeof messages === 'string') {
                errors[field] = messages;
            }
        });

        return errors;
    }
}

SSEmailNotificationScheduleService.$inject = [];
app.service('SSEmailNotificationScheduleService', SSEmailNotificationScheduleService);
